import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Intentar obtener el tema guardado en localStorage
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme');
      // Validar que el tema guardado sea válido
      if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
        return savedTheme;
      }
    }
    return 'system'; // Por defecto usar system
  });

  const [systemTheme, setSystemTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  });

  // Función para aplicar el tema al DOM
  const applyTheme = (currentTheme, currentSystemTheme) => {
    if (typeof window === 'undefined') return;
    
    const root = window.document.documentElement;
    const body = window.document.body;
    
    // Remover clases previas
    root.classList.remove('dark', 'light');
    body.classList.remove('dark', 'light');
    
    // Determinar qué tema aplicar
    let effectiveTheme;
    if (currentTheme === 'system') {
      effectiveTheme = currentSystemTheme;
    } else {
      effectiveTheme = currentTheme;
    }
    
    // Aplicar el tema
    if (effectiveTheme === 'dark') {
      root.classList.add('dark');
      body.classList.add('dark');
    } else {
      root.classList.add('light');
      body.classList.add('light');
    }
    
    // Actualizar meta theme-color para móviles
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', effectiveTheme === 'dark' ? '#1f2937' : '#ffffff');
    } else {
      // Crear meta theme-color si no existe
      const meta = document.createElement('meta');
      meta.name = 'theme-color';
      meta.content = effectiveTheme === 'dark' ? '#1f2937' : '#ffffff';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
  };

  // Effect para escuchar cambios en las preferencias del sistema
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = (e) => {
      const newSystemTheme = e.matches ? 'dark' : 'light';
      setSystemTheme(newSystemTheme);
      
      // Si el tema actual es 'system', aplicar el cambio inmediatamente
      if (theme === 'system') {
        applyTheme('system', newSystemTheme);
      }
    };

    // Configurar el listener
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleSystemThemeChange);
    } else {
      // Fallback para navegadores más antiguos
      mediaQuery.addListener(handleSystemThemeChange);
    }

    // Cleanup
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleSystemThemeChange);
      } else {
        mediaQuery.removeListener(handleSystemThemeChange);
      }
    };
  }, [theme]);

  // Effect principal para aplicar temas
  useEffect(() => {
    // Aplicar tema inicial y guardar en localStorage
    applyTheme(theme, systemTheme);
    
    if (typeof window !== 'undefined') {
      localStorage.setItem('theme', theme);
    }
  }, [theme, systemTheme]);

  // Función para cambiar el tema
  const setCurrentTheme = (newTheme) => {
    // Validar que el tema sea válido
    if (!['light', 'dark', 'system'].includes(newTheme)) {
      console.warn(`Tema inválido: ${newTheme}. Usando 'system' por defecto.`);
      newTheme = 'system';
    }
    
    setTheme(newTheme);
  };

  // Función para obtener el tema efectivo (resuelve 'system' al tema real)
  const getEffectiveTheme = () => {
    if (theme === 'system') {
      return systemTheme;
    }
    return theme;
  };

  // Función para verificar si está en modo oscuro
  const isDarkMode = () => {
    return getEffectiveTheme() === 'dark';
  };

  // Función para toggle entre light y dark (sin system)
  const toggleTheme = () => {
    const effectiveTheme = getEffectiveTheme();
    setCurrentTheme(effectiveTheme === 'dark' ? 'light' : 'dark');
  };

  const value = {
    theme,
    systemTheme,
    effectiveTheme: getEffectiveTheme(),
    isDarkMode: isDarkMode(),
    setTheme: setCurrentTheme,
    toggleTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme debe ser usado dentro de un ThemeProvider');
  }
  return context;
};

export default ThemeContext;